import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { unwrapMaybeWrapped } from '../../lib/unwrapResource';

/**
 * Real-backend Dashboards surface (v1.14.0) — two read-only, aggregate-only
 * endpoints, no mutations. GET /admin/dashboard (permission: view-dashboard)
 * and GET /investor/dashboard (any authenticated investor, scoped
 * automatically). Same defensive-unwrap treatment as every other
 * single-resource endpoint in this app (unwrapMaybeWrapped) — the docs call
 * these "unwrapped" too, and that claim hasn't held up at face value for
 * any other tag yet.
 */

// portfolio.by_status has dynamic keys (real status names like active/
// pending_funding/matured, not the additionalProp1-style placeholders the
// raw schema shows) — passed through as-is rather than mapped into a fixed
// shape, so the UI can render whatever keys actually come back.
function normalizeAdminDashboard(raw) {
  if (!raw) return null;
  const portfolio = raw.portfolio ?? {};
  const investors = raw.investors ?? {};
  const cashflow = raw.cashflow ?? {};
  const activity = raw.recent_activity ?? {};

  return {
    activeAum: portfolio.active_aum ?? '0.00',
    byStatus: portfolio.by_status && typeof portfolio.by_status === 'object' ? portfolio.by_status : {},
    investorsTotal: Number(investors.total ?? 0),
    newThisMonth: Number(investors.new_this_month ?? 0),
    pendingKyc: Number(investors.pending_kyc ?? 0),
    activeSupportTickets: Number(investors.active_support_tickets ?? 0),
    paymentsReceivedMonth: cashflow.payments_received_this_month ?? '0.00',
    paymentsReceivedYtd: cashflow.payments_received_ytd ?? '0.00',
    payoutsMonth: cashflow.payouts_this_month ?? '0.00',
    payoutsYtd: cashflow.payouts_ytd ?? '0.00',
    upcomingMaturities: (cashflow.upcoming_maturities ?? []).map((m) => ({
      investor: m.investor ?? '—',
      plan: m.plan ?? '—',
      principal: m.principal ?? '0.00',
      maturityDate: m.maturity_date ?? null,
    })),
    recentTickets: (activity.support_tickets ?? []).map((t) => ({
      kind: 'ticket',
      investor: t.investor ?? '—',
      category: t.category ?? null,
      status: t.status ?? null,
      createdAt: t.created_at ?? null,
    })),
    recentKyc: (activity.kyc_submissions ?? []).map((k) => ({
      kind: 'kyc',
      investor: k.investor ?? '—',
      status: k.status ?? null,
      createdAt: k.created_at ?? null,
    })),
  };
}

const KYC_STATUS_DISPLAY = {
  active: { label: 'Verified', tone: 'success' },
  approved: { label: 'Verified', tone: 'success' },
  pending_approval: { label: 'Pending', tone: 'warning' },
  pending: { label: 'Pending', tone: 'warning' },
  rejected: { label: 'Rejected', tone: 'danger' },
};

export function kycStatusDisplay(status) {
  return KYC_STATUS_DISPLAY[status] || { label: status ? status.replace(/_/g, ' ') : 'Unknown', tone: 'neutral' };
}

function normalizeInvestorDashboard(raw) {
  if (!raw) return null;
  const portfolio = raw.portfolio ?? {};
  const paymentsMade = raw.payments_made ?? {};
  const lastPayout = raw.last_payout ?? null;

  return {
    totalInvested: portfolio.total_invested ?? '0.00',
    activeInvestmentsCount: Number(portfolio.active_investments_count ?? 0),
    totalInterestEarned: portfolio.total_interest_earned ?? '0.00',
    paymentsMadeTotal: paymentsMade.total ?? '0.00',
    paymentsMadeCount: Number(paymentsMade.count ?? 0),
    lastPayoutAmount: lastPayout?.amount ?? null,
    lastPayoutDate: lastPayout?.date ?? null,
    kycStatus: raw.kyc_status ?? null,
    openSupportTickets: Number(raw.open_support_tickets ?? 0),
  };
}

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: authBaseQuery(),
  tagTypes: [],
  endpoints: (builder) => ({
    // GET /admin/dashboard — permission: view-dashboard. A 403 here is a
    // real, expected outcome (an admin might hold manage-support without
    // view-dashboard) — surfaced by the page as a permission-denied state,
    // not swallowed into an empty dashboard.
    getAdminDashboard: builder.query({
      query: () => ({ url: '/admin/dashboard', method: 'GET' }),
      transformResponse: (response) => normalizeAdminDashboard(unwrapMaybeWrapped(response)),
    }),

    // GET /investor/dashboard — any authenticated investor, scoped
    // automatically server-side, no extra permission to check.
    getInvestorDashboard: builder.query({
      query: () => ({ url: '/investor/dashboard', method: 'GET' }),
      transformResponse: (response) => normalizeInvestorDashboard(unwrapMaybeWrapped(response)),
    }),
  }),
});

export const { useGetAdminDashboardQuery, useGetInvestorDashboardQuery } = dashboardApi;
